import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {useLocation} from 'react-router-dom';
import {debounce} from 'lodash';
import DeleteModal from '../../components/modals/DeleteModal';
import {WebsiteContext} from '../../context/WebsiteProvider';
import {VendorContext} from '../../context/VendorProvider';
import {UserContext} from '../../context/UserProvider';
import VendorModal from '../../components/modals/VendorModal';
import VendorForm from '../vendor/VendorForm';
import VendorFiles from '../vendor/VendorFiles';
import saveIcon from '../../assets/icons/save.svg';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import {useWaveformLoadingWebsites} from '../../components/inputs/useWaveformLoadingWebsites';
import DefaultInputs from '../../views/website/forms/DefaultInputs';

const WebsiteForm = forwardRef(
  (
    {
      website,
      hideFirstComment,
      hasHistory,
      setHasHistory,
      isFormSubmitted,
      onClose,
      vendorLinkDisabled,
      onUpdate,
      postComment,
      commentText,
      isPublic,
      setCommentText,
      setIsPublic = () => {
      },
      resetVendorModal,
    },
    ref
  ) => {
    let {
      refreshWebsites,
      setVendorDropdownOpen,
      vendorSearch,
      setVendorSearch,
      createWebsite,
      updateWebsite,
      getWebsite,
      deleteWebsite,
      isExpanded,
      form,
      setForm,
      blankFormState,
      handleChange,
      selectedWebsite,
      setSelectedWebsite,
      setFormKey,
      setDropdownOpen,
      setVendorSearchResults,
      setSearchResults,
      callWebsiteForRefresh,
      setCallWebsiteForRefresh,
      getActivity,
      showSuccess,
      setShowSuccess,
      showError,
      setShowError,
      text,
      setText,
      products,
      getProducts,
      getWebsiteTeam,
      updateWebsiteTeam,
      createWebsiteTeam,
      setIsTeamGroupWebsite,
      setTeam,
    } = useContext(WebsiteContext);
    const {
      getVendors,
      getVendor,
      selectedLocalVendor,
      setSelectedLocalVendor,
      handleVendorClick,
      isOpen,
      setIsOpen,
      vendorReciprocalAllowed,
    } = useContext(VendorContext);

    const [isVendorOpen, setIsVendorOpen] = useState(false);
    const {darkMode} = useContext(UserContext);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [parentModalKey, setParentModalKey] = useState(Date.now());
    const [showSavedText, setShowSavedText] = useState(false);
    const {startLoading, stopLoading} = useWaveformLoadingWebsites();
    const [initialLoading, setInitialLoading] = useState(true);
    const [loaderWidth] = useState('auto');
    const [placeholder, setPlaceholder] = useState('Name, MLS ID, Member #, Office, DRE#');
    const [initialFormState, setInitialFormState] = useState(blankFormState);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const quickHistoryRef = useRef(null);
    const historyTimeoutRef = useRef(null);
    const [fetchedProducts, setFetchedProducts] = useState([]);
    const location = useLocation();

    const isFormChanged = useMemo(() => {
      return JSON.stringify(form) !== JSON.stringify(initialFormState);
    }, [form, initialFormState]);

    const toggleHistory = () => {
      setIsHistoryOpen((prev) => !prev);
    };

    const handleMouseEnter = () => {
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
        historyTimeoutRef.current = null;
      }
      setIsHistoryOpen(true);
    };

    const handleMouseLeave = () => {
      historyTimeoutRef.current = setTimeout(() => {
        setIsHistoryOpen(false);
      }, 200);
    };

    useEffect(() => {
      const updatePlaceholder = () => {
        if (window.innerWidth < 768) {
          setPlaceholder('');
        } else {
          setPlaceholder('Name, MLS ID, Member #, Office, DRE#');
        }
      };

      updatePlaceholder();
      window.addEventListener('resize', updatePlaceholder);

      return () => {
        window.removeEventListener('resize', updatePlaceholder);
      };
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => {
        setInitialLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }, []);

    const resetForm = () => {
      setForm(initialFormState);
      setIsVendorOpen(false);
    };

    useImperativeHandle(ref, () => ({
      resetForm,
      resetVendorModal: () => setIsVendorOpen(false),
    }));

    const closeNotification = () => {
      setShowSuccess(false);
      setShowError(false);
    };

    const searchVendors = useCallback(
      async (value) => {
        try {
          const allVendors = await getVendors();
          return allVendors.filter(
            (vendor) =>
              (vendor.licensee.toLowerCase().includes(value.toLowerCase()) ||
                vendor.vendorCode.toLowerCase().includes(value.toLowerCase())) &&
              vendor.rowKey !== (website?.rowKey || '')
          );
        } catch (error) {
          throw new Error('Failed to fetch vendor info');
        }
      },
      [getVendors, website?.rowKey]
    );

    useEffect(() => {
      const debouncedSearchVendors = debounce(async (value) => {
        try {
          const results = await searchVendors(value);
          setVendorSearchResults(results);
        } catch (error) {
          console.error('Failed to fetch vendor info', error);
          setVendorSearchResults([]);
        }
      }, 300);

      const handleVendorSearch = (value) => {
        setVendorSearch(value);
        if (!value) {
          setVendorSearchResults([]);
          return;
        }
        debouncedSearchVendors(value);
      };

      handleVendorSearch(vendorSearch);

      return () => {
        debouncedSearchVendors.cancel();
      };
    }, [vendorSearch, searchVendors, setVendorSearch, setVendorSearchResults]);

    useEffect(() => {
      if (showSavedText) {
        const timeout = setTimeout(() => {
          setShowSavedText(false);
        }, 3000);
        return () => {
          clearTimeout(timeout);
        };
      }
    }, [showSavedText]);

    const openInNewTab = (url) => {
      const newWindow = window.open(`http://${url}`, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    };

    const openDeleteModal = (e) => {
      e.preventDefault();
      setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
      setShowDeleteModal(false);
    };

    const handleDelete = async () => {
      await deleteWebsite(website.rowKey);
      setShowDeleteModal(false);
      setSelectedWebsite(null);
      setFormKey(null);
      if (typeof onClose === 'function') {
        onClose();
      }
    };

    const handleStatusChange = (e) => {
      const newStatus = e.target.value;
      let newTermDate = '';
      let newStartDate = form.startDate;

      if (newStatus === 'Inactive') {
        newTermDate = new Date().toISOString().split('T')[0];
      } else if (newStatus === 'Active' && !form.rowKey && !form.startDate) {
        newStartDate = new Date().toISOString().split('T')[0];
      }

      setForm((prevForm) => ({
        ...prevForm,
        status: newStatus,
        termDate: newTermDate,
        startDate: newStartDate,
      }));
    };

    useEffect(() => {
      if (!form.rowKey && form.status === 'Active' && !form.startDate) {
        setForm((prevForm) => ({
          ...prevForm,
          startDate: new Date().toISOString().split('T')[0],
        }));
      }
    }, [form.rowKey, form.status, form.startDate, setForm]);

    const handleProductChange = (e) => {
      const selectedProductId = parseInt(e.target.value, 10);
      if (!isNaN(selectedProductId)) {
        setForm((prevForm) => ({
          ...prevForm,
          products: [selectedProductId],
        }));
      }
    };

    useEffect(() => {
      if (form.status !== 'Inactive' && form.termDate) {
        setForm((prevForm) => ({
          ...prevForm,
          termDate: '',
        }));
      }

      const fetchProducts = async () => {
        try {
          const productList = await getProducts();
          if (Array.isArray(productList)) {
            setFetchedProducts(productList);
            setForm((prevForm) => ({
              ...prevForm,
              products: prevForm.products.length ? prevForm.products : [productList[0]?.id],
            }));
          }
        } catch (error) {
          console.error('Failed to fetch products', error);
        }
      };
      fetchProducts();
    }, [form.status, form.termDate, setForm, getProducts]);

    useEffect(() => {
      if (fetchedProducts.length && !form.products.length) {
        setForm((prevForm) => ({
          ...prevForm,
          products: [fetchedProducts[0]?.id],
        }));
      }
    }, [fetchedProducts, form.products.length, setForm]);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!isFormChanged) return;

      const requiredFields = {
        vendor: form.vendor,
        domainName: form.domainName,
        memberNumber: form.memberNumber,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !value)
        .map(([key]) => (key === 'memberNumber' ? 'agent' : key === 'domainName' ? 'domain' : key));

      if (missingFields.length > 0) {
        let formattedMessage;
        if (missingFields.length === 1) {
          formattedMessage = `Please add the following field: ${missingFields[0]}`;
        } else {
          formattedMessage = `Please add the following fields: ${missingFields.join(', ')}`;
        }

        setShowError(true);
        setText(formattedMessage);
        setTimeout(() => {
          setShowError(false);
          setText('');
        }, 3000);
        setShowSpinner(false);
        return;
      }

      try {
        setShowSpinner(true);
        const token = localStorage.getItem('token');
        const newForm = {...form};
        const teamProductId = products.find((product) => product.name === 'Team/Group Website')?.id;

        let updatedWebsite;
        if (form.rowKey) {
          updatedWebsite = await updateWebsite({
            ...newForm,
            vendorId: newForm.vendorId,
          });

          if (newForm.products.includes(teamProductId) && newForm.firstName && newForm.lastName) {
            const existingTeam = await getWebsiteTeam(form.rowKey, token);
            const primaryMember = {
              firstName: newForm.firstName,
              lastName: newForm.lastName,
              mlsId: newForm.mlsId,
              memberNumber: newForm.memberNumber,
              office: newForm.office,
              status: newForm.status,
              email: `${newForm.firstName}.${newForm.lastName}@example.com`,
            };

            if (!existingTeam || !existingTeam.members?.length) {
              const newTeam = {
                id: 1,
                siteId: form.rowKey,
                primary: parseInt(newForm.memberNumber, 10),
                memberIds: [parseInt(newForm.memberNumber, 10)],
                members: [primaryMember],
                teamCount: 1,
              };
              await createWebsiteTeam(form.rowKey, newTeam, token);
              setTeam(newTeam);
              setIsTeamGroupWebsite(true);
            } else {
              const updatedMembers = existingTeam.members.filter(
                (m) => parseInt(m.memberNumber, 10) !== existingTeam.primary
              );
              updatedMembers.unshift(primaryMember);
              const updatedTeam = {
                ...existingTeam,
                primary: parseInt(newForm.memberNumber, 10),
                memberIds: updatedMembers.map((m) => parseInt(m.memberNumber, 10)),
                members: updatedMembers,
                teamCount: updatedMembers.length,
              };
              await updateWebsiteTeam(form.rowKey, updatedTeam, token);
              setTeam(updatedTeam);
              setIsTeamGroupWebsite(true);
            }
          } else {
            setIsTeamGroupWebsite(false);
          }

          if (commentText.trim()) {
            await postComment(form.rowKey, {
              comment: commentText.trim(),
              public: isPublic,
            });
            setCommentText('');
            setIsPublic(false);
          }
        } else {
          const createdWebsite = await createWebsite({
            ...newForm,
            vendorId: newForm.vendorId,
          });
          updatedWebsite = await getWebsite(createdWebsite.rowKey);
          newForm.rowKey = createdWebsite.rowKey;

          if (newForm.products.includes(teamProductId) && newForm.firstName && newForm.lastName) {
            const primaryMember = {
              firstName: newForm.firstName,
              lastName: newForm.lastName,
              mlsId: newForm.mlsId,
              memberNumber: newForm.memberNumber,
              office: newForm.office,
              status: newForm.status,
              email: `${newForm.firstName}.${newForm.lastName}@example.com`,
            };
            const newTeam = {
              id: 1,
              siteId: createdWebsite.rowKey,
              primary: parseInt(newForm.memberNumber, 10),
              memberIds: [parseInt(newForm.memberNumber, 10)],
              members: [primaryMember],
              teamCount: 1,
            };
            await createWebsiteTeam(createdWebsite.rowKey, newTeam, token);
            setTeam(newTeam);
            setIsTeamGroupWebsite(true);
          } else {
            setIsTeamGroupWebsite(false);
          }

          if (commentText.trim()) {
            await postComment(createdWebsite.rowKey, {
              comment: commentText.trim(),
              public: isPublic,
            });
            setCommentText('');
            setIsPublic(false);
          }
        }

        if (updatedWebsite?.rowKey) {
          const history = await getActivity(updatedWebsite.rowKey);
          setHasHistory(history && Array.isArray(history) && history.length > 0);
        }

        if (typeof refreshWebsites === 'function') {
          await refreshWebsites();
        }

        setShowSuccess(true);
        setText(form.rowKey ? 'Website updated successfully' : 'Website created successfully');
        setTimeout(() => {
          setShowSuccess(false);
          setText('');
        }, 3000);

        const updatedFormState = {
          ...form,
          ...updatedWebsite,
          rowKey: updatedWebsite?.rowKey || form.rowKey,
          vendor: form.vendor,
        };
        setForm(updatedFormState);
        setInitialFormState(updatedFormState);
      } catch (error) {
        setShowError(true);
        setText(`An error occurred: ${error.message}`);
        setTimeout(() => {
          setShowError(false);
          setText('');
        }, 3000);
      } finally {
        setShowSpinner(false);
        if (isFormSubmitted) {
          onClose();
        }
      }
    };

    const handleVendorInputClick = (e) => {
      e.preventDefault();
      console.log('handleVendorInputClick called');
      if (vendorLinkDisabled) {
        console.log('vendorLinkDisabled is true, exiting');
        return;
      }
      if (website && website.vendorId) {
        console.log('Fetching vendor with ID:', website.vendorId);
        getVendor(website.vendorId).then((vendor) => {
          console.log('Vendor fetched:', vendor);
          handleVendorClick(website.vendorId, null, false);
          setIsVendorOpen(true);
          setSelectedLocalVendor(vendor);
        });
      } else {
        console.log('No website or vendorId present');
      }
    };

    useEffect(() => {
      if (location.pathname.startsWith('/websites')) {
        setIsVendorOpen(false);
      }
    }, [location.pathname]);

    useEffect(() => {
      const localBlankFormState = {
        products: [],
        memberNumber: '',
        licenseNumber: '',
        status: 'Active',
        firstName: '',
        lastName: '',
        mlsId: '',
        memberAOR: '',
        broker: {
          firstName: '',
          lastName: '',
          mlsId: '',
          memberNumber: '',
          licenseNumber: '',
          status: '',
        },
      };

      if (website) {
        const {
          products,
          firstName,
          lastName,
          mlsId,
          memberNumber,
          licenseNumber,
          status,
          memberAOR,
          broker,
          ...websiteData
        } = website;

        const productIds = Array.isArray(products)
          ? products.map((product) => Number(product))
          : [];

        setForm((prevForm) => ({
          ...prevForm,
          ...localBlankFormState,
          ...websiteData,
          products: productIds.length ? productIds : [],
          memberNumber: memberNumber || '',
          licenseNumber: licenseNumber || '',
          status: status || 'Active',
          firstName: firstName || '',
          lastName: lastName || '',
          mlsId: mlsId || '',
          memberAOR: memberAOR || '',
          broker: broker || {
            firstName: firstName || '',
            lastName: lastName || '',
            mlsId: mlsId || '',
            memberNumber: memberNumber || '',
            licenseNumber: licenseNumber || '',
            status: status || '',
          },
        }));
      } else {
        setForm({
          ...localBlankFormState,
          products: [],
          broker: {
            firstName: '',
            lastName: '',
            mlsId: '',
            memberNumber: '',
            licenseNumber: '',
            status: '',
          },
        });
      }
    }, [website, setForm]);

    useEffect(() => {
      if (initialLoading) {
        setInitialFormState(form);
      }
    }, [form, initialLoading, website]);

    useEffect(() => {
      if (isFormSubmitted) {
        setForm(initialFormState);
        setSelectedLocalVendor(null);
        setVendorSearch('');
        setVendorSearchResults([]);
        setSearchResults([]);
        setDropdownOpen(false);
        setVendorDropdownOpen(false);
        setIsVendorOpen(false);
        onClose();
        setParentModalKey(Date.now());
      }
    }, [
      isFormSubmitted,
      setForm,
      initialFormState,
      setSelectedLocalVendor,
      setVendorSearch,
      setVendorSearchResults,
      setSearchResults,
      setDropdownOpen,
      setVendorDropdownOpen,
      onClose,
      setParentModalKey,
    ]);

    const startSpinner = () => {
      setShowSpinner(true);
    };

    useEffect(() => {
      if (showSpinner) {
        const timer = setTimeout(() => {
          setShowSpinner(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }, [showSpinner]);

    useEffect(() => {
    }, [vendorLinkDisabled]);

    const searchDropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
          setDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [setDropdownOpen]);

    const handleRefreshYes = async () => {
      setCallWebsiteForRefresh(false);

      const updatedWebsite = await getWebsite(selectedWebsite.rowKey);

      if (updatedWebsite) {
        const {
          firstName,
          lastName,
          mlsId,
          memberNumber,
          licenseNumber,
          status,
          memberAOR,
          broker,
          Products,
          ...websiteData
        } = updatedWebsite;

        setForm((prevForm) => ({
          ...prevForm,
          ...blankFormState,
          ...websiteData,
          memberNumber: memberNumber || '',
          status: status || 'Active',
          firstName: firstName || '',
          lastName: lastName || '',
          mlsId: mlsId || '',
          memberAOR: memberAOR || '',
          broker: broker || {
            firstName: firstName || '',
            lastName: lastName || '',
            mlsId: mlsId || '',
            memberNumber: memberNumber || '',
            licenseNumber: licenseNumber || '',
            status: status || '',
          },
        }));
        setInitialFormState(updatedWebsite);
      }
    };

    return (
      <div
        key={parentModalKey}
        style={{maxWidth: '1100px', maxHeight: '600px', overflowY: 'auto'}}
        className="w-full mx-auto bg-white dark:bg-stone-900 md:pl-2 md:pt-1 lg:p-6"
      >
        <form onSubmit={handleSubmit} className="mt-1.5">
          <DefaultInputs
            form={form}
            handleChange={handleChange}
            initialLoading={initialLoading}
            darkMode={darkMode}
            loaderWidth={loaderWidth}
            website={website}
            openInNewTab={openInNewTab}
            handleStatusChange={handleStatusChange}
            handleProductChange={handleProductChange}
            products={products}
            isExpanded={isExpanded}
            hasHistory={hasHistory}
            toggleHistory={toggleHistory}
            isHistoryOpen={isHistoryOpen}
            quickHistoryRef={quickHistoryRef}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            placeholder={placeholder}
            vendorLinkDisabled={vendorLinkDisabled}
            selectedLocalVendor={selectedLocalVendor}
            setSelectedLocalVendor={setSelectedLocalVendor}
            setForm={setForm}
            startLoading={startLoading}
            stopLoading={stopLoading}
            vendorReciprocalAllowed={vendorReciprocalAllowed}
            handleVendorInputClick={handleVendorInputClick}
          />

          <div className={`my-4 flex items-center ${isExpanded ? 'pt-4' : ''}`}>
            <div className="mt-1 flex-grow flex" style={{width: '100%'}}>
              <button
                type="submit"
                className={`mt-6 inline-flex w-32 items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-semibold text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 ${
                  !isFormChanged ? 'cursor-not-allowed opacity-50' : ''
                }`}
                onClick={startSpinner}
                disabled={!isFormChanged}
              >
                {showSpinner ? (
                  <div
                    className="mr-2.5 inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-gray-300"></div>
                ) : (
                  <img src={saveIcon} alt="Save" className="mr-1.5 inline-block h-4 w-4"/>
                )}
                {showSpinner ? 'Saving' : form.rowKey ? 'Update' : 'Create'}
              </button>
              <button
                onClick={openDeleteModal}
                type="button"
                className={`ml-2 mt-6 inline-flex w-32 items-center justify-center rounded-md border px-4 py-2 text-base font-semibold text-white md:px-3 ${
                  form.status === 'Active'
                    ? 'cursor-not-allowed border-gray-500 bg-gray-500'
                    : 'border-red-500 bg-red-500 hover:bg-red-700'
                }`}
                disabled={form.status === 'Active'}
                style={{transition: 'background-color 0.9s'}}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="mr-1 inline-block h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                  />
                </svg>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </form>

        {showDeleteModal && (
          <DeleteModal
            title="Are you sure?"
            onCancel={() => closeDeleteModal(false)}
            onDelete={handleDelete}
          />
        )}
        {isVendorOpen && !vendorLinkDisabled && (
          <VendorModal
            isOpen={isOpen}
            onClose={() => {
              console.log('VendorModal onClose called');
              setIsVendorOpen(false);
              setIsOpen(false);
            }}
            vendor={selectedLocalVendor}
          >
            {selectedLocalVendor && <VendorForm vendor={selectedLocalVendor}/>}
            <VendorFiles vendorId={selectedLocalVendor ? selectedLocalVendor.id : null}/>
          </VendorModal>
        )}
        {showSuccess && (
          <SuccessNotification show={showSuccess} onClose={closeNotification} text={text}/>
        )}
        {showError && <ErrorNotification show={showError} onClose={closeNotification} text={text}/>}
        {callWebsiteForRefresh && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center pt-8 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-stone-950 bg-opacity-75"></div>
              </div>
              <span
                className="hidden sm:inline-block sm:h-screen sm:align-middle"
                aria-hidden="true"
              />
              <div
                className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all dark:bg-stone-800 sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                <div className="flex h-full flex-col">
                  <div
                    className="flex flex-grow items-center justify-center bg-white px-4 py-6 dark:bg-stone-800 sm:p-6">
                    <div className="sm:flex sm:items-center">
                      <div className="text-center sm:text-left">
                        <h3
                          className="pt-6 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-300"
                          id="modal-headline"
                        >
                          Changes have been made, please refresh.
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white px-4 py-3 dark:bg-stone-800 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      onClick={handleRefreshYes}
                      type="button"
                      className="mt-3 inline-flex w-28 justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:ml-2 sm:mt-0 sm:text-sm"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default WebsiteForm;